import React, { useEffect, useState } from 'react';
import { CheckCircle2, Download, FileText, Loader2, X } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import type { Localized } from '../../types/notice';
import type { BriefingItem } from '../../utils/territorialBriefing';
interface CouncilReportDialogProps {
  open: boolean;
  onClose: () => void;
  scopeLabel: Localized | null;
  periodLabel: Localized;
  priorities: BriefingItem[];
  kpis: {
    label: Localized;
    value: string;
  }[];
}

/** Preview of the council report, generated from the current analytics view. */
export function CouncilReportDialog({
  open,
  onClose,
  scopeLabel,
  periodLabel,
  priorities,
  kpis
}: CouncilReportDialogProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [generating, setGenerating] = useState(true);
  const [downloaded, setDownloaded] = useState(false);
  useEffect(() => {
    if (!open) return;
    setGenerating(true);
    setDownloaded(false);
    const id = window.setTimeout(() => setGenerating(false), 1200);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);
  if (!open) return null;
  const scope = scopeLabel ? scopeLabel[lang] : t('Toute la ville', 'Whole city');
  return <div className="fixed inset-0 z-[1000] bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose} data-id="element-5328" data-mp="2a2x2f7n2hukb">
      <div role="dialog" aria-modal="true" aria-labelledby="council-report-title" className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col font-body" onClick={e => e.stopPropagation()} data-id="element-5329" data-mp="2e378ekav0kz6">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100" data-id="element-5330" data-mp="2t1svugr3q4x3">
          <h2 id="council-report-title" className="text-base font-bold text-[#0F172A] font-heading flex items-center gap-2" data-id="element-5331" data-mp="2xs9c08mb44n5">
            <FileText className="w-4 h-4 text-[#4F46E5]" data-id="element-5332" data-mp="37ohvcilcsi3h" />
            {t('Rapport pour le conseil municipal', 'City council report')}
          </h2>
          <button onClick={onClose} aria-label={t('Fermer', 'Close')} className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100" data-id="element-5333" data-mp="32n26efg21rft">
            <X className="w-4 h-4" data-id="element-5334" data-mp="2st1y64ph862v" />
          </button>
        </div>

        {generating ? <div className="flex flex-col items-center justify-center gap-3 py-20 text-sm text-slate-500" data-id="element-5335" data-mp="2orgnmq0f03vu">
            <Loader2 className="w-6 h-6 text-[#4F46E5] animate-spin" data-id="element-5336" data-mp="2jwsw0ia46td1" />
            {t('ATNA rédige le rapport…', 'ATNA is writing the report…')}
          </div> : <div className="overflow-y-auto px-6 py-6" data-id="element-5337" data-mp="2a20z7gocwajb">
            <div className="border border-slate-200 rounded-lg p-6 space-y-6 bg-slate-50/50" data-id="element-5338" data-mp="251wfziw2d1pe">
              <header className="border-b border-slate-200 pb-4" data-id="element-5339" data-mp="2101cc7qnuc8g">
                <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-5340" data-mp="211w0dux8akgr">
                  Ville de Brossard · {t('Séance du conseil', 'Council meeting')}
                </div>
                <h3 className="text-lg font-bold text-[#0F172A] font-heading mt-1" data-id="element-5341" data-mp="kl6rdfwvuulk">
                  {t('Suivi des demandes citoyennes', 'Citizen request tracking')}
                </h3>
                <p className="text-sm text-slate-500 mt-1" data-id="element-5342" data-mp="uffibz69mfhm">
                  {scope} · {periodLabel[lang]}
                </p>
              </header>

              <section data-id="element-5343" data-mp="1s2wjr7xibpd">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-5344" data-mp="1tm9p8yag6mx">
                  {t('Faits saillants', 'Highlights')}
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-id="element-5345" data-mp="bixhms9fv598">
                  {kpis.map(k => <div key={k.label.fr} className="bg-white border border-slate-200 rounded-lg p-3" data-id="element-5346" data-mp="3a7mmohdaze07">
                      <div className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-5347" data-mp="3f6fzs8ooh2zu">{k.value}</div>
                      <div className="text-xs text-slate-500" data-id="element-5348" data-mp="1ksbwaaj82f5t">{k.label[lang]}</div>
                    </div>)}
                </div>
              </section>

              <section data-id="element-5349" data-mp="1uhvgpkml2h2s">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-5350" data-mp="1ui3tlciphmdz">
                  {t('Enjeux et recommandations', 'Issues and recommendations')}
                </h4>
                <ol className="space-y-3" data-id="element-5351" data-mp="24e6tsctzzaya">
                  {priorities.map((p, i) => <li key={p.id} className="text-sm" data-id="element-5352" data-mp="1zfdfwjasthxl" data-mpi={i}>
                      <div className="font-semibold text-[#0F172A]" data-id="element-5353" data-mp="2dg7ph59l1oxu">
                        {i + 1}. {p.title[lang]}
                      </div>
                      <div className="text-slate-600 mt-0.5" data-id="element-5354" data-mp="28i64gesljfjq">{p.nextStep[lang]}</div>
                    </li>)}
                </ol>
              </section>
            </div>
          </div>}

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100" data-id="element-5355" data-mp="e4n28rnmf8s3">
          {downloaded && <span className="mr-auto text-sm text-emerald-700 inline-flex items-center gap-1.5" data-id="element-5356" data-mp="2psrxwdgnt049">
              <CheckCircle2 className="w-4 h-4" data-id="element-5357" data-mp="2urcywf3qjntr" />
              {t('Rapport téléchargé', 'Report downloaded')}
            </span>}
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50" data-id="element-5358" data-mp="2kyxhbiwcp19e">
            {t('Fermer', 'Close')}
          </button>
          <button onClick={() => setDownloaded(true)} disabled={generating} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] hover:bg-[#4338CA] rounded-lg disabled:opacity-50" data-id="element-5359" data-mp="2c0lpxenntzfw">
            <Download className="w-4 h-4" data-id="element-5360" data-mp="3sebyaxltb9vc" />
            {t('Télécharger (PDF)', 'Download (PDF)')}
          </button>
        </div>
      </div>
    </div>;
}